import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Tabs } from "@/components/kit";
import { AREA_NAMES, BodyFigure } from "@/components/bodymap/BodyFigure";
import { BodyPatterns } from "@/components/bodymap/BodyPatterns";

export const FEELINGS = [["tight", "#7C5CBF"], ["heavy", "#5C7CFA"], ["buzzing", "#E8833A"], ["aching", "#D9577F"], ["hot", "#E85D5D"], ["numb", "#8A94A6"]];
const color = (f) => FEELINGS.find((x) => x[0] === f)?.[1];

const SpotEditor = ({ area, spot, onChange, onRemove }) => (
  <div className="mm-card p-4 mm-fade-up" data-testid="body-spot-editor">
    <div className="flex justify-between items-center mb-3">
      <div className="font-semibold">{AREA_NAMES[area]}: how does it feel?</div>
      <button onClick={onRemove} className="text-mm-muted p-1" aria-label="Remove this area" data-testid="body-spot-remove"><X size={16} /></button>
    </div>
    <div className="flex flex-wrap gap-2 mb-4">
      {FEELINGS.map(([f, c]) => (
        <button key={f} onClick={() => onChange({ ...spot, feeling: f })} className={`mm-chip capitalize ${spot.feeling === f ? "mm-chip-on" : ""}`} style={spot.feeling === f ? { background: c, borderColor: c } : undefined} data-testid={`body-feeling-${f}`}>{f}</button>
      ))}
    </div>
    <div className="text-sm text-mm-muted mb-2">How strong? {spot.intensity} of 5</div>
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onChange({ ...spot, intensity: n })} className={`w-11 h-11 rounded-full font-semibold border-2 transition-colors ${spot.intensity === n ? "text-white" : "border-mm-line"}`}
          style={spot.intensity === n ? { background: color(spot.feeling), borderColor: color(spot.feeling) } : undefined} data-testid={`body-intensity-${n}`}>{n}</button>
      ))}
    </div>
  </div>
);

const Matches = ({ skills, onAgain }) => (
  <div className="mm-fade-up" data-testid="body-matches">
    <h2 className="text-base md:text-lg font-bold mb-3">These might help right now</h2>
    <div className="grid gap-3">
      {skills.map((s) => (
        <Link key={s.id} to={`/coping?skill=${s.id}`} className="mm-card mm-card-hover p-4 flex gap-3 items-start" data-testid={`body-match-${s.id}`}>
          <div className="text-2xl" aria-hidden>{s.icon}</div>
          <div><div className="font-semibold">{s.name} <span className="text-xs text-mm-muted font-normal">· {s.duration}</span></div><p className="text-sm text-mm-muted mt-0.5">{s.reason}</p></div>
        </Link>
      ))}
    </div>
    <button onClick={onAgain} className="mm-btn-ghost mt-4" data-testid="body-map-again">Map again</button>
  </div>
);

const MapTab = ({ fromCheckin }) => {
  const [side, setSide] = useState("front");
  const [spots, setSpots] = useState({});
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skills, setSkills] = useState(null);
  const tap = (a) => { setSel(a); setSpots((s) => (s[a] ? s : { ...s, [a]: { feeling: "tight", intensity: 3 } })); };
  const fills = Object.fromEntries(Object.entries(spots).map(([a, s]) => [a, { color: color(s.feeling), opacity: 0.25 + s.intensity * 0.13 }]));
  const find = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/body-map", { spots: Object.entries(spots).map(([area, s]) => ({ area, ...s })), from_checkin: fromCheckin });
      setSkills(data.skills);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (skills) return <Matches skills={skills} onAgain={() => { setSkills(null); setSpots({}); setSel(null); }} />;
  return (
    <div className="grid md:grid-cols-2 gap-6 items-start">
      <div className="mm-card p-5">
        <div className="flex justify-center gap-2 mb-3">{["front", "back"].map((s) => <button key={s} onClick={() => setSide(s)} className={`mm-chip capitalize ${side === s ? "mm-chip-on" : ""}`} data-testid={`body-side-${s}`}>{s}</button>)}</div>
        <BodyFigure side={side} fills={fills} onTap={tap} selected={sel} />
        <p className="text-center text-sm text-mm-muted mt-3">Tap where you feel tension or discomfort</p>
      </div>
      <div className="space-y-4">
        {sel && spots[sel] && <SpotEditor area={sel} spot={spots[sel]} onChange={(v) => setSpots((s) => ({ ...s, [sel]: v }))} onRemove={() => { setSpots(({ [sel]: _, ...rest }) => rest); setSel(null); }} />}
        {Object.keys(spots).length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="body-spot-list">
            {Object.entries(spots).map(([a, s]) => <button key={a} onClick={() => setSel(a)} className="mm-chip" data-testid={`body-spot-chip-${a}`}><span className="w-2.5 h-2.5 rounded-full" style={{ background: color(s.feeling) }} />{AREA_NAMES[a]} · {s.feeling} {s.intensity}</button>)}
          </div>
        )}
        {!Object.keys(spots).length && <div className="mm-soft p-4 text-sm" data-testid="body-map-empty">Notice your body for a moment. Where do you feel it? Head, jaw, chest, stomach, shoulders… tap one or more.</div>}
        <button onClick={find} disabled={busy || !Object.keys(spots).length} className="mm-btn w-full" data-testid="body-map-find">{busy && <Loader2 size={16} className="animate-spin" />}Find what might help</button>
      </div>
    </div>
  );
};

export default function BodyMap() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "patterns" ? "patterns" : "map");
  useEffect(() => { window.scrollTo(0, 0); }, [tab]);
  return (
    <div className="max-w-4xl" data-testid="body-map-page">
      <PageHeader back title="Body Map" subtitle="Notice where stress lives in your body, and find what might help" />
      <Tabs items={[{ id: "map", name: "Map how I feel" }, { id: "patterns", name: "My patterns" }]} value={tab} onChange={setTab} testPrefix="body-tab" />
      {tab === "map" ? <MapTab fromCheckin={params.get("from") === "checkin"} /> : <BodyPatterns />}
    </div>
  );
}
